import React, { useState } from 'react';
import { Artist } from '../types/artist';
import { useArtistSearch } from '../hooks/useArtistSearch';
import { useFuzzySearch } from '../hooks/useFuzzySearch';

interface Props {
  onArtistSelect: (artist: Artist) => void;
}

export const ArtistSearch: React.FC<Props> = ({ onArtistSelect }) => {
  const { query, setQuery, results, isLoading, error } = useArtistSearch(400);
  const fuzzyResults = useFuzzySearch(results, query);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSelectArtist = (artist: Artist) => {
    setQuery(artist.name);
    setFocusedIndex(-1);
    setDropdownOpen(false);
    onArtistSelect(artist);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (fuzzyResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, fuzzyResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleSelectArtist(fuzzyResults[focusedIndex]);
    } else if (e.key === 'Escape') {
      setFocusedIndex(-1);
      setDropdownOpen(false);
    }
  };

  const showResults = dropdownOpen && query.trim().length >= 2;
  const listboxId = 'artist-search-listbox';

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <label htmlFor="artist-search" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#333' }}>
          Search for an artist or band
        </label>
        <input
          id="artist-search"
          type="search"
          value={query}
          onChange={e => { setQuery(e.target.value); setFocusedIndex(-1); setDropdownOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="e.g. The Cure, Radiohead, Nine Inch Nails..."
          aria-label="Search artists"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={showResults && fuzzyResults.length > 0}
          aria-activedescendant={focusedIndex >= 0 ? `artist-option-${focusedIndex}` : undefined}
          style={{
            width: '100%',
            padding: '10px 14px',
            fontSize: '16px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {isLoading && (
          <div
            role="status"
            aria-live="polite"
            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '13px' }}
          >
            Searching…
          </div>
        )}
      </div>

      {error && (
        <div style={{ color: '#c00', marginTop: '8px', fontSize: '14px' }} role="alert">{error}</div>
      )}

      {showResults && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Artist search results"
          style={{
            listStyle: 'none',
            margin: '4px 0 0',
            padding: 0,
            border: fuzzyResults.length > 0 ? '1px solid #ddd' : 'none',
            borderRadius: '8px',
            background: '#fff',
            boxShadow: fuzzyResults.length > 0 ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {fuzzyResults.length === 0 && !isLoading && (
            <li style={{ padding: '12px 14px', color: '#888', fontSize: '14px' }} aria-live="polite">No artists found.</li>
          )}
          {fuzzyResults.map((artist, i) => (
            <li
              id={`artist-option-${i}`}
              key={artist.id}
              role="option"
              aria-selected={focusedIndex === i}
              onClick={() => handleSelectArtist(artist)}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                backgroundColor: focusedIndex === i ? '#f0fdf4' : 'transparent',
                borderBottom: i < fuzzyResults.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}
              onMouseEnter={() => setFocusedIndex(i)}
            >
              <div style={{ fontWeight: 500, color: '#1DB954' }}>{artist.name}</div>
              {artist.disambiguation && (
                <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{artist.disambiguation}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

